// ConsumerActivityReport.jsx - Track consumer engagement and identify at-risk consumers
import React, { useState, useEffect, useContext, useCallback } from "react";
import { AuthContext } from "../../contexts/AuthContext";
import { auditApi } from "../../utils/api";
import secureAxios from "../../utils/secureAxios";
import { toast } from "react-toastify";

const ConsumerActivityReport = () => {
  const { user } = useContext(AuthContext);

  const tenant_id = user?.tenant_id;

  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState([]);
  const [summary, setSummary] = useState(null);
  const [filters, setFilters] = useState({
    customer_id: "",
    status_filter: "all", // all, active, at_risk, urgent, inactive, no_activity
  });
  const [customers, setCustomers] = useState([]);

  // Load filter options
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const customerRes = await secureAxios.get("/api/customers", {
          params: { tenant_id },
        });
        setCustomers(customerRes.data || []);
      } catch (err) {
        console.error("Error loading filter options:", err);
      }
    };
    if (tenant_id) loadFilterOptions();
  }, [tenant_id]);

  // Load report data
  const loadReport = useCallback(async () => {
    if (!tenant_id) return;
    setLoading(true);
    try {
      const response = await secureAxios.get(
        "/api/business-reports/consumer-activity",
        {
          params: {
            tenant_id,
            customer_id: filters.customer_id || undefined,
            status_filter: filters.status_filter,
          },
        }
      );

      if (response.data.success) {
        setReportData(response.data.data || []);
        setSummary(response.data.summary || null);

        await auditApi.logEntry({
          action: "GENERATE_CONSUMER_ACTIVITY_REPORT",
          entity_type: "REPORT",
          entity_id: null,
          contains_phi: true,
          action_status: "SUCCESS",
          purpose_of_use: "OPERATIONS",
        });
      }
    } catch (err) {
      console.error("Error loading consumer activity report:", err);
      toast.error("Failed to load consumer activity report");
    } finally {
      setLoading(false);
    }
  }, [tenant_id, filters]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const formatNumber = (num, decimals = 1) => {
    return Number(num || 0).toFixed(decimals);
  };

  const getActivityBadge = (status) => {
    switch (status) {
      case "active":
        return { color: "success", text: "Active" };
      case "at_risk":
        return { color: "warning", text: "At Risk" };
      case "urgent":
        return { color: "orange", text: "Urgent", customStyle: { backgroundColor: "#fd7e14", color: "white" } };
      case "inactive":
        return { color: "danger", text: "Inactive" };
      case "no_activity":
        return { color: "secondary", text: "No Activity" };
      default:
        return { color: "secondary", text: "Unknown" };
    }
  };

  const getDaysSinceColor = (days) => {
    if (days === null || days === undefined) return "secondary";
    if (days <= 30) return "success";
    if (days <= 60) return "warning";
    if (days <= 90) return "orange";
    return "danger";
  };

  return (
    <div className="container-fluid">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">
            <i className="bi bi-people me-2"></i>
            Consumer Activity Report
          </h2>
          <p className="text-muted mb-0">
            Track consumer engagement and identify at-risk consumers
          </p>
        </div>
        <button
          className="btn btn-outline-primary"
          onClick={loadReport}
          disabled={loading}
        >
          <i className="bi bi-arrow-clockwise me-1"></i>
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-4">
        <div className="card-body">
          <div className="row g-3 align-items-end">
            <div className="col-md-4">
              <label className="form-label">Customer</label>
              <select
                className="form-select"
                value={filters.customer_id}
                onChange={(e) =>
                  setFilters({ ...filters, customer_id: e.target.value })
                }
              >
                <option value="">All Customers</option>
                {customers.map((c) => (
                  <option key={c.customer_id} value={c.customer_id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-4">
              <label className="form-label">Activity Status</label>
              <select
                className="form-select"
                value={filters.status_filter}
                onChange={(e) =>
                  setFilters({ ...filters, status_filter: e.target.value })
                }
              >
                <option value="all">All Consumers</option>
                <option value="active">Active (0-30 Days)</option>
                <option value="at_risk">At Risk (31-60 Days)</option>
                <option value="urgent">Urgent (61-90 Days)</option>
                <option value="inactive">Inactive (90+ Days)</option>
                <option value="no_activity">No Activity Ever</option>
              </select>
            </div>
            <div className="col-md-4">
              <button
                className="btn btn-outline-secondary w-100"
                onClick={() =>
                  setFilters({
                    customer_id: "",
                    status_filter: "all",
                  })
                }
              >
                <i className="bi bi-x-circle me-1"></i>
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="row g-3 mb-4">
          <div className="col-6 col-md">
            <div className="card bg-primary text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Total Consumers
                </h6>
                <h4 className="card-title mb-0">{summary.total_consumers}</h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div className="card bg-success text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">Active</h6>
                <h6 className="card-subtitle mb-1 opacity-75 small">0-30 Days</h6>
                <h4 className="card-title mb-0">{summary.active}</h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div className="card bg-warning text-dark h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">At Risk</h6>
                <h6 className="card-subtitle mb-1 opacity-75 small">31-60 Days</h6>
                <h4 className="card-title mb-0">{summary.at_risk}</h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div className="card text-white h-100" style={{ backgroundColor: "#fd7e14" }}>
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">Urgent</h6>
                <h6 className="card-subtitle mb-1 opacity-75 small">61-90 Days</h6>
                <h4 className="card-title mb-0">{summary.urgent}</h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div className="card bg-danger text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  Inactive
                </h6>
                <h6 className="card-subtitle mb-1 opacity-75 small">90+ Days</h6>
                <h4 className="card-title mb-0">{summary.inactive}</h4>
              </div>
            </div>
          </div>
          <div className="col-6 col-md">
            <div className="card bg-secondary text-white h-100">
              <div className="card-body text-center py-2">
                <h6 className="card-subtitle mb-1 opacity-75 small">
                  No Activity
                </h6>
                <h4 className="card-title mb-0">{summary.no_activity}</h4>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      )}

      {/* Data Table */}
      {!loading && reportData.length > 0 && (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-striped table-hover mb-0">
              <thead className="table-dark">
                <tr>
                  <th>Consumer</th>
                  <th>Customer</th>
                  <th className="text-center">Status</th>
                  <th>Last Service</th>
                  <th className="text-center">Days Since</th>
                  <th className="text-end">Total Hours</th>
                  <th className="text-end">This Month</th>
                  <th className="text-center">Active Auths</th>
                </tr>
              </thead>
              <tbody>
                {reportData.map((row, index) => {
                  const statusBadge = getActivityBadge(row.activity_status);
                  const daysBadgeColor = getDaysSinceColor(row.days_since_last_service);
                  return (
                    <tr key={index}>
                      <td className="fw-bold">{row.consumer_name}</td>
                      <td className="text-muted">{row.customer_name}</td>
                      <td className="text-center">
                        {statusBadge.customStyle ? (
                          <span className="badge" style={statusBadge.customStyle}>
                            {statusBadge.text}
                          </span>
                        ) : (
                          <span className={`badge bg-${statusBadge.color}`}>
                            {statusBadge.text}
                          </span>
                        )}
                      </td>
                      <td>
                        {row.last_service_date ? (
                          new Date(row.last_service_date).toLocaleDateString()
                        ) : (
                          <span className="text-muted fst-italic">Never</span>
                        )}
                      </td>
                      <td className="text-center">
                        {row.days_since_last_service !== null ? (
                          daysBadgeColor === "orange" ? (
                            <span className="badge" style={{ backgroundColor: "#fd7e14", color: "white" }}>
                              {row.days_since_last_service} days
                            </span>
                          ) : (
                            <span className={`badge bg-${daysBadgeColor}`}>
                              {row.days_since_last_service} days
                            </span>
                          )
                        ) : (
                          <span className="badge bg-secondary">N/A</span>
                        )}
                      </td>
                      <td className="text-end">
                        {formatNumber(row.total_hours, 1)}
                      </td>
                      <td className="text-end">
                        {formatNumber(row.hours_this_month, 1)}
                      </td>
                      <td className="text-center">
                        <span
                          className={`badge ${
                            row.active_auths > 0 ? "bg-success" : "bg-secondary"
                          }`}
                        >
                          {row.active_auths}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* No Data */}
      {!loading && reportData.length === 0 && (
        <div className="text-center py-5">
          <i
            className="bi bi-inbox text-muted"
            style={{ fontSize: "3rem" }}
          ></i>
          <h5 className="mt-3">No Data Available</h5>
          <p className="text-muted">
            No consumers found matching your criteria.
          </p>
        </div>
      )}
    </div>
  );
};

export default ConsumerActivityReport;